import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { DollarSign, TrendingUp, AlertCircle, FileDown, Loader2, Building2, CalendarDays } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { managerApi, type ManagerDashboardResponse, type ManagerBookingSummary } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/earnings")({
  component: EarningsPage,
});

const fmt = (n: number) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(n);

export default function EarningsPage() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState<ManagerDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    managerApi.dashboard(user.id)
      .then(res => setDashboard(res))
      .catch(() => setDashboard(null))
      .finally(() => setLoading(false));
  }, [user?.id]);

  const serviceChargeRate = dashboard?.commissionRate ?? 0.10;
  const serviceChargePct = Math.round(serviceChargeRate * 100);

  const bookings = dashboard?.recentBookings ?? [];

  const stats = useMemo(() => {
    const confirmed = bookings.filter(b => b.status.toLowerCase() === "confirmed");
    const gross = confirmed.reduce((s, b) => s + Number(b.totalAmount), 0);
    const serviceCharge = dashboard?.totalCommission ?? Math.round(gross * serviceChargeRate);
    const net = dashboard?.netEarnings ?? (gross - serviceCharge);
    return { confirmed, gross, serviceCharge, net };
  }, [bookings, dashboard, serviceChargeRate]);

  const monthly = useMemo(() => {
    const map: Record<string, number> = {};
    for (const b of stats.confirmed) {
      const key = b.startDate.slice(0, 7);
      map[key] = (map[key] ?? 0) + Number(b.totalAmount);
    }
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, gross]) => ({
        label: new Date(month + "-01").toLocaleDateString("en-NG", { month: "short", year: "numeric" }),
        gross,
        net: Math.round(gross * (1 - serviceChargeRate)),
      }));
  }, [stats.confirmed, serviceChargeRate]);

  const isEmpty = !loading && bookings.length === 0;

  return (
    <div className="mx-auto max-w-6xl animate-fade-up space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-extrabold">Earnings</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Your revenue summary and payout history.</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 rounded-full"
          disabled={exporting || bookings.length === 0}
          onClick={async () => {
            setExporting(true);
            try {
              await managerApi.exportRevenue();
            } catch {
              /* error shown by apiDownload */
            } finally {
              setExporting(false);
            }
          }}
        >
          {exporting ? <Loader2 className="size-4 animate-spin" /> : <FileDown className="size-4" />}
          Export
        </Button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border py-20 text-center">
          <Loader2 className="mb-3 size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading earnings…</p>
        </div>
      ) : isEmpty ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border py-20 text-center">
          <DollarSign className="mb-3 size-12 text-muted-foreground/40" />
          <p className="text-lg font-semibold">No earnings yet</p>
          <p className="mt-1 text-sm text-muted-foreground max-w-xs">
            Confirmed bookings will appear here once guests start reserving your venues.
          </p>
        </div>
      ) : (
        <>
          {/* Summary cards — separated venue/event/total */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[
              { icon: TrendingUp,  label: "Total Revenue",    value: fmt(dashboard?.totalRevenue ?? stats.gross),       sub: "venue + event", color: "text-foreground" },
              { icon: Building2,   label: "Venue Revenue",    value: fmt(dashboard?.venueRevenue ?? 0),                 sub: "bookings & extensions", color: "text-blue-600" },
              { icon: CalendarDays,label: "Event Revenue",    value: fmt(dashboard?.eventRevenue ?? 0),                 sub: "ticket sales", color: "text-emerald-600" },
              { icon: AlertCircle, label: `Service Charge (${serviceChargePct}%)`, value: `−${fmt(dashboard?.totalCommission ?? stats.serviceCharge)}`, sub: "Platform fee", color: "text-red-500" },
              { icon: DollarSign,  label: "Net Earnings",     value: fmt(dashboard?.netEarnings ?? stats.net),          sub: "After service charge", color: "text-green-600" },
              { icon: Building2,   label: "Venue Net",        value: fmt(dashboard?.venueNetEarnings ?? 0),             sub: "after commission", color: "text-blue-500" },
              { icon: CalendarDays,label: "Event Net",        value: fmt(dashboard?.eventNetEarnings ?? 0),             sub: "after commission", color: "text-emerald-500" },
            ].map(c => (
              <div key={c.label} className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
                <div className="grid size-10 place-items-center rounded-lg bg-accent text-primary mb-3">
                  <c.icon className="size-5" />
                </div>
                <p className={`text-xl font-bold ${c.color}`}>{c.value}</p>
                <p className="text-xs font-medium text-foreground mt-1">{c.label}</p>
                <p className="text-[11px] text-muted-foreground">{c.sub}</p>
              </div>
            ))}
          </div>

          {/* Monthly breakdown */}
          {monthly.length > 0 && (
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
              <h2 className="font-display text-base font-semibold mb-4">Monthly Breakdown</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/60 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      <th className="pb-3 pr-6">Month</th>
                      <th className="pb-3 pr-6 text-right">Gross</th>
                      <th className="pb-3 pr-6 text-right">Service Charge</th>
                      <th className="pb-3 text-right">Net</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {monthly.map(r => (
                      <tr key={r.label} className="hover:bg-accent/30">
                        <td className="py-3 pr-6 font-medium">{r.label}</td>
                        <td className="py-3 pr-6 text-right">{fmt(r.gross)}</td>
                        <td className="py-3 pr-6 text-right text-red-500">−{fmt(Math.round(r.gross * serviceChargeRate))}</td>
                        <td className="py-3 text-right font-semibold text-green-600">{fmt(r.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Individual booking earnings */}
          <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
            <h2 className="font-display text-base font-semibold mb-4">Booking Earnings</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    <th className="pb-3 pr-4">Title</th>
                    <th className="pb-3 pr-4">Guest</th>
                    <th className="pb-3 pr-4">Date</th>
                    <th className="pb-3 pr-4 text-right">Amount</th>
                    <th className="pb-3 text-right">Payout</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {stats.confirmed.map(b => (
                    <tr key={b.bookingId} className="hover:bg-accent/30 transition-colors">
                      <td className="py-2.5 pr-4 font-medium">{b.venueName}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{b.guestName}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground text-xs">{b.startDate.slice(0, 10)}</td>
                      <td className="py-2.5 pr-4 text-right">{fmt(Number(b.totalAmount))}</td>
                      <td className="py-2.5 text-right font-semibold text-green-600">{fmt(Math.round(Number(b.totalAmount) * (1 - serviceChargeRate)))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
